import React, { useRef, useState, FormEvent, ChangeEvent } from 'react';
import styled from 'styled-components';
import { theme } from './theme';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { ReactComponent as CloseIcon } from './assets/icons/close.svg';

// Styled components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.modal};
`;

const FormContainer = styled.div`
  position: relative;
  background: ${theme.colors.bgDark};
  border: 2px solid ${theme.colors.gold};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  color: ${theme.colors.white};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const FormTitle = styled.h2`
  color: ${theme.colors.gold};
  font-size: ${theme.fontSizes.xl};
  margin-bottom: 1.5rem;
  padding-right: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormRow = styled.div<{ isCheckbox?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${props => props.isCheckbox && `
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  `}
`;

const Label = styled.label<{ isCheckbox?: boolean }>`
  color: ${theme.colors.gold};
  font-size: ${theme.fontSizes.base};

  ${props => props.isCheckbox && `
    order: 2;
    cursor: pointer;
  `}

  a {
    color: ${theme.colors.gold};
    font-weight: ${theme.fontWeights.bold};
    text-decoration: underline;
  }
`;

const Input = styled.input<{ isCheckbox?: boolean }>`
  background: ${theme.colors.cardBg};
  border: 1px solid ${theme.colors.gold};
  color: ${theme.colors.white};
  padding: 0.75rem;
  font-size: ${theme.fontSizes.base};
  border-radius: ${theme.borderRadius.sm};
  outline: none;

  &:focus {
    border-color: ${theme.colors.goldAlt};
  }

  ${props => props.isCheckbox && `
    order: 1;
    width: auto;
    cursor: pointer;
  `}
`;

const SubmitButton = styled.button`
  background: ${theme.colors.gold};
  border: none;
  color: ${theme.colors.bgDark};
  padding: 1rem;
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  margin-top: 1rem;
  transition: background ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.goldAlt};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.colors.gold};
  font-size: ${theme.fontSizes.lg};
`;

interface CallMeFormProps {
  onClose: () => void;
  sourcePath: string;
}

interface FormData {
  firstName: string;
  phone: string;
  email: string;
}

export default function CallMeForm({ onClose, sourcePath }: CallMeFormProps): React.ReactElement {
  const { t } = useTranslation('forms');
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState<FormData>({
    firstName: '',
    phone: '',
    email: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    if (!termsAccepted) {
      alert(t('callMeForm.validation.gdprRequired'));
      return;
    }

    setIsSubmitting(true);

    if (!formRef.current) {
      setIsSubmitting(false);
      return;
    }

    emailjs
      .sendForm(
        'service_6x99yxh',
        'template_bu29wgd',
        formRef.current,
        'fWkAlUO_qyEgz2hG9'
      )
      .then(
        () => {
          setIsSubmitting(false);
          setIsSent(true);
          setForm({ firstName: '', phone: '', email: '' });
          setTermsAccepted(false);

          // Auto close after 3 seconds
          setTimeout(() => {
            onClose();
          }, 3000);
        },
        (err) => {
          setIsSubmitting(false);
          alert(t('callMeForm.status.error', { error: err.text }));
        }
      );
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <FormContainer>
        <CloseButton onClick={onClose} disabled={isSubmitting}>
          <CloseIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
        </CloseButton>

        {!isSent ? (
          <>
            <FormTitle>{t('callMeForm.title')}</FormTitle>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <FormRow>
                <Label htmlFor="firstName">{t('callMeForm.labels.fullName')}</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>

              <FormRow>
                <Label htmlFor="phone">{t('callMeForm.labels.phone')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>

              <FormRow>
                <Label htmlFor="email">{t('callMeForm.labels.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </FormRow>

              <FormRow isCheckbox>
                <Input
                  isCheckbox
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  disabled={isSubmitting}
                  required
                />
                <Label isCheckbox htmlFor="termsAccepted">
                  <a href={t('callMeForm.gdpr.link')} target='_blank' rel="noopener noreferrer">{t('callMeForm.gdpr.text')}</a>
                </Label>
              </FormRow>

              <input name="formType" value="CallMe" hidden readOnly />
              <input name="sourcePath" value={sourcePath} hidden readOnly />
              <input name="email_to" value="dominik.branka@blogic.cz" hidden readOnly />

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('callMeForm.buttons.submitting') : t('callMeForm.buttons.submit')}
              </SubmitButton>
            </Form>
          </>
        ) : (
          <SuccessMessage>
            <p>{t('callMeForm.status.success')}</p>
          </SuccessMessage>
        )}
      </FormContainer>
    </Overlay>
  );
}
